/* import __COLOCATED_TEMPLATE__ from './seller-profile.hbs'; */
import Component from '@glimmer/component';
import { createCache, getValue } from '@glimmer/tracking/primitives/cache';
import { inject as service } from '@ember/service';
import { task } from "ember-concurrency";
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { handleErrorResponseWithModal } from 'sh-www/utils/handles-errors';
import { APPLICATION } from 'sh-www/models/application';

class SellerProfileComponent extends Component {
  @service('commands.user-commands') userCommandService;
  @service('commands.application-commands') applicationCommandService;
  @service ('commands.note-commands') noteCommandService;
  @service session;

  @tracked activeApplicationsCount = null;
  @tracked completedApplicationsCount = null;

  #currentCompanyConnection = createCache(() => {
    return this.args.user.companySellerConnections.find(({ companyId }) => this.companyId == companyId);
  });

  get companyId() {
    return this.session.currentUser.company.id;
  }

  get currentUser() {
    return this.session.currentUser;
  }

  get currentCompanyConnection() {
    return getValue(this.#currentCompanyConnection);
  }

  // Returned active applications could be different than companyConnection value depending on teams restrictions
  get activeProjectsCount() {
    return this.activeApplicationsCount ?? this.currentCompanyConnection?.activeProjects;
  }

  // completedProjects value on companyConnection will always be correct since there are no teams restrictions on the GET request
  get completedProjectsCount() {
    return this.currentCompanyConnection?.completedProjects ?? this.completedApplicationsCount;
  }

  @action
  bootstrap(user, company) {
    this.loadActiveApplications.perform(user.id, 1);
    this.loadCompletedApplications.perform(-1, user.id, 1);
    this.loadProfileNotes.perform(user);

    if (company.canUseCompanyTeams && !this.session.currentUser.isAdmin) {
      this.loadCurrentUserCompanyTeams.perform();
    }
  }

  @task
  *loadActiveApplications(userId, currentPage) {
    try {
      const response = yield this.applicationCommandService.loadApplications(
        undefined,
        APPLICATION.SIMPLE_STATUS.IN_PROGRESS_BUYER,
        undefined,
        false,
        undefined,
        currentPage,
        10,
        undefined,
        'assignment,brand.image,company-team,thread.messages,pitch',
        userId,
        this.companyId,
        null
      );

      this.activeApplicationsCount = response.pagination.total;
      return response;
    } catch(error) {
      handleErrorResponseWithModal(
        this,
        error,
        "There was a problem loading projects. Please try again or contact support if the problem continues.",
        `loadActiveApplications -- error loading applications for user id: ${userId}`,
      );
    }
  }

  @task
  *loadCompletedApplications(activeServiceFilterId, userId, currentPage) {
    const serviceFilter = activeServiceFilterId > 0 ? activeServiceFilterId : null;

    try {
      const response = yield this.applicationCommandService.loadApplications(
        undefined,
        APPLICATION.SIMPLE_STATUS.COMPLETE,
        undefined,
        false,
        undefined,
        currentPage,
        5,
        undefined,
        'internal-review.reviewer,assignment,services,company-team,projectdigest.review.reviewer,projectdigest.company,' +
        'projectdigest.oembed,projectdigest.projectdigest.projectdigest-verifications:verified_or_latest,projectdigest.services,' +
        'projectdigest.classifications',
        userId,
        this.companyId,
        serviceFilter,
        true
      );
      this.completedApplicationsCount = response.pagination.total;
      return response;
    } catch(error) {
      handleErrorResponseWithModal(
        this,
        error,
        "There was a problem loading projects. Please try again or contact support if the problem continues.",
        `loadCompletedApplications -- error loading applications for user id: ${userId}`,
      );
    }
  }

  @task
  *loadProfileNotes(user) {
    try {
      return yield this.noteCommandService.loadNotesOnSubject(
        user,
        this.currentUser,
        'application.company-team,created-by-user,created-about-user'
      );
    } catch(error) {
      handleErrorResponseWithModal(
        this,
        error,
        "There was a problem loading company notes. Please try again or contact support if the problem continues.",
        `loadProfileNotes -- error loading notes for company id: ${this.companyId}`,
      );
    }
  }

  @task
  *loadCurrentUserCompanyTeams() {
    try {
      return yield this.userCommandService.getUser(this.currentUser.id, ['company-teams']);
    } catch(error) {
      handleErrorResponseWithModal(
        this,
        error,
        "There was a problem loading company teams data. Please try again or contact support if the problem continues.",
        `loadCurrentUserCompanyTeams -- error loading company teams data for user id: ${this.currentUser.id}`,
      );
    }
  }
}

export default SellerProfileComponent;
